import { formatRewardsBonus } from '@imprint-payments/imprint-lib';
import {
  colors,
  IconComponentType,
  Icons,
  ImpRewardsDisplay,
  ImpText,
} from '@imprint-payments/imprint-ui';
import { useEffect, useState } from 'react';

import { useImpState } from '../../app/hooks';
import { getOfferUUIDs } from '../../utils/functionUtils';
import { merchantInfoByProductUUID } from '../../utils/productUUIDConstants';

interface OfferDataProps {
  amount: string;
  title: string;
  badge?: {
    children: string;
    leadingIcon: IconComponentType;
  };
  content?: React.ReactNode;
}

interface CreateOfferFromBonusParams {
  currBonus: any;
  includeBadge: boolean;
}

const OfferContent = ({ text }: { text: string }) => {
  return (
    <ImpText>
      <span
        style={{ color: colors.content.onSurfaceVariant }}
        dangerouslySetInnerHTML={{ __html: text }}
      ></span>
    </ImpText>
  );
};

export const useRewardsInfo = () => {
  const { merchantData, language } = useImpState();
  const { rewardDetails } = merchantData;
  const [offerData, setOfferData] = useState<OfferDataProps[]>([]);

  useEffect(() => {
    const offerData: OfferDataProps[] = [];

    const createOfferFromBonus = ({
      currBonus,
      includeBadge,
    }: CreateOfferFromBonusParams): OfferDataProps => {
      const baseOffer: OfferDataProps = {
        amount: formatRewardsBonus(currBonus),
        title: currBonus.title,
      };

      // only include the content key if the text isn't empty
      if (currBonus.items.text) {
        baseOffer.content = <OfferContent text={currBonus.items.text} />;
      }

      if (includeBadge) {
        baseOffer.badge = {
          children: language.limitedTimeOfferBadge,
          leadingIcon: Icons.timer,
        };
      }

      return baseOffer;
    };

    if (rewardDetails.rewardsBonus) {
      const { rewardsBonus } = rewardDetails;
      const offerUUIDsSet = new Set(getOfferUUIDs());

      for (const currBonus of rewardsBonus) {
        const currBonusOfferConfigUUIDs = currBonus.offerConfigUUIDs;

        if (currBonusOfferConfigUUIDs) {
          for (const offerUUID of currBonusOfferConfigUUIDs) {
            if (
              (offerUUIDsSet.size === 0 && offerUUID === 'default') ||
              offerUUIDsSet.has(offerUUID)
            ) {
              offerData.push(
                createOfferFromBonus({ currBonus, includeBadge: true })
              );
              break;
            }
          }
        } else {
          offerData.push(
            createOfferFromBonus({ currBonus, includeBadge: false })
          );
        }
      }
    }

    const annualFee =
      merchantInfoByProductUUID[merchantData.productUUID].annualFee;
    offerData.push({
      amount: annualFee,
      title: language.annualFeeSuperscript,
    });

    setOfferData(offerData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rewardDetails]);

  return offerData;
};

export function RewardsDisplay() {
  const { merchantData } = useImpState();
  const offerData = useRewardsInfo();

  return (
    <ImpRewardsDisplay
      cardImageSrc={merchantData.cardImg}
      cardName={merchantData.rewardDetails.cardName}
      isLoading={offerData.length === 1}
      rewardsBonus={offerData}
    />
  );
}
